import { useEffect, useState } from "react";
import { CheckCircle2, Vote } from "lucide-react";
import { toast } from "sonner";
import { fetchVotes, castVote } from "@/lib/api";

const VOTER_KEY = "bask_voter_id";
const VOTED_KEY = "bask_voted_symbol";

const getVoterId = () => {
  let id = localStorage.getItem(VOTER_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(VOTER_KEY, id);
  }
  return id;
};

export default function VoteSection() {
  const [data, setData] = useState(null);
  const [myVote, setMyVote] = useState(localStorage.getItem(VOTED_KEY));
  const [voting, setVoting] = useState("");

  useEffect(() => {
    fetchVotes().then(setData).catch(() => {});
  }, []);

  const vote = async (symbol) => {
    if (myVote || voting) return;
    setVoting(symbol);
    try {
      const result = await castVote(symbol, getVoterId());
      setData(result);
      setMyVote(symbol);
      localStorage.setItem(VOTED_KEY, symbol);
      toast.success(`Vote recorded for ${symbol}. Community suggestions shape the basket shortlist.`);
    } catch (e) {
      if (e?.response?.status === 409) {
        setMyVote(symbol);
        localStorage.setItem(VOTED_KEY, symbol);
        toast.info("This browser has already voted.");
      } else {
        toast.error("Vote failed — try again.");
      }
    } finally {
      setVoting("");
    }
  };

  const options = data?.options ?? [];
  const total = data?.total_votes ?? 0;

  return (
    <section className="py-24 px-6 bg-[#090612] border-b border-white/5 relative overflow-hidden" data-testid="vote-section">
      <div className="absolute inset-0 sol-glow pointer-events-none opacity-40" />
      <div className="max-w-4xl mx-auto relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="mono-font text-xs uppercase tracking-widest text-[#14F195] font-semibold">Community Signal</span>
          <h2 className="display-font uppercase text-3xl md:text-5xl text-white mt-2">Vote The Next Basket Asset</h2>
          <p className="text-sm text-[#a8a1c2] mt-3">
            Which Solana asset should be considered next? Community votes are suggestions for the shortlist — not guaranteed investment decisions. Final composition follows published rules.
          </p>
        </div>

        <div className="glass-panel rounded-3xl p-6 md:p-8">
          <div className="flex items-center justify-between mb-6">
            <span className="mono-font text-xs text-[#9b93b8] uppercase tracking-wider flex items-center gap-2">
              <Vote size={14} className="text-[#14F195]" /> Live Community Poll
            </span>
            <span className="mono-font text-xs text-white" data-testid="vote-total">
              <span className="text-[#14F195] font-bold">{total.toLocaleString()}</span> total votes
            </span>
          </div>

          <div className="space-y-3">
            {options.map((o) => {
              const isMine = myVote === o.symbol;
              return (
                <button
                  key={o.symbol}
                  data-testid={`vote-btn-${o.symbol.toLowerCase()}`}
                  onClick={() => vote(o.symbol)}
                  disabled={!!myVote || !!voting}
                  className={`w-full text-left p-4 rounded-2xl border transition-all duration-300 relative overflow-hidden group
                    ${isMine ? "border-[#14F195]/60 bg-[#14F195]/5" : "border-white/10 bg-white/[0.03] hover:border-[#9945FF]/50"}
                    ${myVote && !isMine ? "opacity-70 cursor-default" : "cursor-pointer"}`}
                >
                  {/* percentage fill */}
                  <div
                    className="absolute inset-y-0 left-0 transition-all duration-700 ease-out"
                    style={{ width: `${o.pct}%`, background: `linear-gradient(90deg, ${o.color}14, ${o.color}2e)` }}
                  />
                  <div className="relative flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs mono-font shrink-0"
                        style={{ background: `${o.color}22`, border: `1px solid ${o.color}88`, color: o.color, boxShadow: `0 0 10px ${o.color}33` }}>
                        {o.symbol}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-white flex items-center gap-2">
                          {o.name}
                          {isMine && (
                            <span className="flex items-center gap-1 text-[10px] mono-font text-[#14F195]" data-testid="your-vote-badge">
                              <CheckCircle2 size={12} /> Your vote
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-[#9b93b8] mono-font">{o.votes.toLocaleString()} votes</div>
                      </div>
                    </div>
                    <span className="mono-font text-lg font-bold" style={{ color: o.color }} data-testid={`vote-pct-${o.symbol.toLowerCase()}`}>
                      {o.pct}%
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          <p className="text-[11px] text-[#ffaa00] mono-font mt-6 text-center" data-testid="vote-disclaimer">
            * One vote per browser. Votes are non-binding community suggestions — basket decisions follow the published eligibility and governance rules.
          </p>
        </div>
      </div>
    </section>
  );
}
